import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { UnreadBadge } from 'components/SideBar/UnreadBadge';
import {
  authStrings,
  commonStrings,
  organizationManagementStrings,
  organizationStrings,
} from 'constants/strings';
import { orgPath, routes } from 'constants/routes';
import { useAuth } from 'contexts/AuthContext';
import { useMe } from 'hooks/useMe';
import { useNotificationSummaryByOrganization } from 'hooks/useNotificationSummary';
import { signOutCompletely } from 'lib/signOut';
import type { TMeMembership } from 'types';
import {
  readLastOrganizationId,
  writeLastOrganizationId,
} from 'utils/organizationStorage';

type SelectOrganizationLocationState = { notAvailable?: boolean } | null;

function unreadText(unreadCount: number | null): string {
  if (unreadCount === null) return '';
  return unreadCount > 0
    ? organizationStrings.unreadLabel(unreadCount)
    : organizationStrings.unreadNone;
}

export default function SelectOrganization() {
  const { user } = useAuth();
  const { data: me, isLoading, isError, isFetching, refetch } = useMe();
  const unreadByOrganizationId = useNotificationSummaryByOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as SelectOrganizationLocationState;
  const lastOrganizationId = user ? readLastOrganizationId(user.uid) : null;

  const handleSelect = (organizationId: number) => {
    if (user) {
      writeLastOrganizationId(user.uid, organizationId);
    }
    navigate(orgPath(organizationId, routes.LINE));
  };

  const handleSignOut = () => {
    void signOutCompletely();
  };

  // 所属拒否の直後は無効化前の /me が残るため、単一所属の自動遷移は再取得の完了を待つ
  const isAwaitingFreshMe = me?.memberships.length === 1 && isFetching;

  if (isLoading || isAwaitingFreshMe) {
    return <p className="p-6 text-sm text-muted">{commonStrings.loadingEllipsis}</p>;
  }

  if (isError || !me) {
    return (
      <div
        role="alert"
        className="grid place-items-center gap-3 p-6 text-sm text-danger"
      >
        <p>{organizationStrings.loadFailed}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => refetch()}
        >
          {commonStrings.actions.retry}
        </Button>
      </div>
    );
  }

  if (me.memberships.length === 1) {
    return (
      <Navigate
        to={orgPath(me.memberships[0].organizationId, routes.LINE)}
        replace
      />
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-surface text-ink">
      <div className="relative grid min-h-screen place-items-center px-5 py-12 sm:px-6">
        <section className="w-full max-w-[440px] rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-soft backdrop-blur-sm motion-safe:animate-float-in sm:p-8">
          <div className="grid gap-5">
            <header className="grid gap-1 text-center">
              <h1 className="text-xl font-display text-ink">
                {organizationStrings.selectTitle}
              </h1>
              <p className="text-sm text-muted">
                {organizationStrings.selectDescription}
              </p>
            </header>

            {state?.notAvailable && (
              <Alert>{organizationStrings.notAvailable}</Alert>
            )}

            {me.memberships.length === 0 ? (
              <p className="text-sm text-muted">
                {organizationStrings.noMemberships}
              </p>
            ) : (
              <ul className="grid gap-2">
                {me.memberships.map((membership: TMeMembership) => {
                  const unreadCount =
                    unreadByOrganizationId.get(membership.organizationId) ?? null;
                  const isLastSelected =
                    lastOrganizationId === membership.organizationId;

                  return (
                    <li key={membership.organizationId}>
                      {/* Button は子を inline-flex の span で包み行の配置が効かないため、素の button で並べる */}
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 rounded-[var(--btn-radius)] border border-ink/15 bg-white/80 px-4 py-3 text-left text-ink transition hover:-translate-y-0.5 hover:border-ink/30 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                        onClick={() => handleSelect(membership.organizationId)}
                      >
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-ink text-xs font-bold text-white">
                          {membership.organizationName.charAt(0)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-ink">
                            {membership.organizationName}
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted">
                            <span>
                              {
                                organizationManagementStrings.planLabels[
                                  membership.plan
                                ]
                              }
                            </span>
                            {isLastSelected && (
                              <span className="rounded-full bg-accent-soft px-2 py-0.5 font-semibold text-ink">
                                {organizationStrings.lastSelected}
                              </span>
                            )}
                            <span>{unreadText(unreadCount)}</span>
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {unreadCount !== null && unreadCount > 0 && (
                            <UnreadBadge count={unreadCount} />
                          )}
                          <span className="text-xs font-semibold text-accent">
                            {organizationStrings.selectAction}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <Button type="button" size="sm" variant="secondary" onClick={handleSignOut}>
              {authStrings.signOut}
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
}
